export interface RFXReferenceDocument {
  id: string;
  filename: string;
  description: string;
  category: 'overview' | 'legal_terms' | 'certification' | 'vendor_info' | 'sow' | 'evaluation' | 'contract' | 'matrix' | 'pricing' | 'submission_guide';
  fileFormat: 'docx' | 'pdf' | 'xlsx';
  mandatory: boolean;
  scoringWeight?: string;
  keyRequirementsSummary: string;
  complianceCheckpoints: string[];
  extractedClauses?: string[];
  defaultSelected: boolean;
}

export const DEFAULT_16_RFX_REFERENCE_DOCUMENTS: RFXReferenceDocument[] = [
  {
    id: 'doc_01',
    filename: '01_RFP_Overview.docx',
    description: 'RFP Overview',
    category: 'overview',
    fileFormat: 'docx',
    mandatory: true,
    scoringWeight: 'Prerequisite (Pass/Fail)',
    keyRequirementsSummary: 'Administrative overview establishing project purpose, timetable, procurement authority, submission deadlines, questions protocol, and mandatory intent-to-bid procedures.',
    complianceCheckpoints: [
      'Submission before official closing deadline',
      'Designation of authorized single point of contact (POC)',
      'Adherence to written communication and addenda receipt verification rules'
    ],
    extractedClauses: [
      'All proposals must be submitted electronically via the secure state e-procurement repository.',
      'Late submissions will be rejected without evaluation.',
      'Amendments and official Q&A addenda are binding upon all offerors.'
    ],
    defaultSelected: true
  },
  {
    id: 'doc_02',
    filename: '02_Attachment_A_-_RFP_Terms_and_Conditions.docx',
    description: 'Attachment A - RFP Terms and Conditions',
    category: 'legal_terms',
    fileFormat: 'docx',
    mandatory: true,
    scoringWeight: 'Contractual Compliance (Pass/Fail)',
    keyRequirementsSummary: 'General state terms and conditions, indemnification, insurance minimums ($2M general aggregate, $5M cyber liability), governing jurisdiction, intellectual property rights, and termination for cause/convenience.',
    complianceCheckpoints: [
      'Verification of cyber liability and errors & omissions insurance',
      'Affirmation of state jurisdiction and venue compliance',
      'Acceptance of data breach indemnification clauses'
    ],
    extractedClauses: [
      'Offeror shall maintain Commercial General Liability with limits no less than $2,000,000 per occurrence.',
      'All deliverables developed under the contract become the sole property of the State.',
      'State reserves right to terminate for convenience upon thirty (30) days written notice.'
    ],
    defaultSelected: true
  },
  {
    id: 'doc_03',
    filename: '03_Attachment_A1_OMES_FormCP004_Non-Collusion_Certification.docx',
    description: 'Attachment A1 - OMES Form CP004 Non-Collusion Certification',
    category: 'certification',
    fileFormat: 'docx',
    mandatory: true,
    scoringWeight: 'Mandatory Submission (Pass/Fail)',
    keyRequirementsSummary: 'Sworn affidavit certifying that bid prices were arrived at independently without collusion, communication, or consultation with any competing offerors.',
    complianceCheckpoints: [
      'Authorized corporate officer notarized signature',
      'No contact or price-fixing with competing bidders',
      'Independent price determination declaration'
    ],
    extractedClauses: [
      'I hereby affirm that the prices and terms quoted have been arrived at independently and without collusion.',
      'No attempt has been made to induce any other entity to submit or not submit a proposal.'
    ],
    defaultSelected: true
  },
  {
    id: 'doc_04',
    filename: '04_Attachment_A2_Form_CP076_Responding_Bidder_Information.pdf',
    description: 'Attachment A2 - Form CP076 Responding Bidder Information',
    category: 'vendor_info',
    fileFormat: 'pdf',
    mandatory: true,
    scoringWeight: 'Administrative Check (Pass/Fail)',
    keyRequirementsSummary: 'Primary vendor legal profile: FEIN, Unique Entity Identifier (SAM.gov UEI), CAGE code, State Vendor ID, corporate officers, HUB/MWBE status, and executive point of contact.',
    complianceCheckpoints: [
      'Active registration in State Vendor Portal and SAM.gov',
      'Valid Employer Identification Number (EIN / FEIN)',
      'Designated Contract Executive Officer with binding authority'
    ],
    extractedClauses: [
      'Offeror must be in good standing with the Secretary of State and Department of Taxation.',
      'Provide 3 relevant commercial/public sector past performance references.'
    ],
    defaultSelected: true
  },
  {
    id: 'doc_05',
    filename: '05_Attachment_B_-_Scope_of_Work.docx',
    description: 'Attachment B - Scope of Work',
    category: 'sow',
    fileFormat: 'docx',
    mandatory: true,
    scoringWeight: 'Technical Evaluation (40% Total Score)',
    keyRequirementsSummary: 'Comprehensive Statement of Work (SOW) defining functional deliverables, technical cloud architecture, microservices integration, data security/NIST SP 800-53 standards, project milestones, and SLA guarantees.',
    complianceCheckpoints: [
      'Detailed work breakdown structure (WBS) with agile sprint milestones',
      'Architectural compliance with NIST/FedRAMP Moderate baseline',
      '99.99% system availability SLA and 15-minute P1 incident MTTR'
    ],
    extractedClauses: [
      'Contractor must deliver an end-to-end cloud-native system within 180 calendar days of Notice to Proceed.',
      'All source code and infrastructure-as-code scripts must pass automated security vulnerability scanning.',
      'Weekly executive status reports and bi-weekly milestone deliverable demonstrations are mandatory.'
    ],
    defaultSelected: true
  },
  {
    id: 'doc_06',
    filename: '06_Attachment_C-__RFP_Evaluation_Plan.docx',
    description: 'Attachment C - RFP Evaluation Plan',
    category: 'evaluation',
    fileFormat: 'docx',
    mandatory: true,
    scoringWeight: 'Scoring Framework Reference',
    keyRequirementsSummary: 'Detailed evaluation matrix and weighting rubric: Technical Approach & Architecture (40 pts), Cost Proposal & Best Value (30 pts), Past Performance & Team Experience (20 pts), and Small Business / HUB Participation (10 pts).',
    complianceCheckpoints: [
      'Align response headings directly with evaluation scoring criteria',
      'Provide clear quantitative evidence for all past performance metrics',
      'Exceed baseline technical expectations to capture maximum points'
    ],
    extractedClauses: [
      'Proposals scoring under 70 points on technical merit will be disqualified from cost evaluation.',
      'Cost points will be calculated via Normalized Inverse Formula: (Lowest Cost / Bid Cost) * 30 pts.',
      'Oral presentations and live sandbox demonstrations may be requested for top 3 shortlisted offerors.'
    ],
    defaultSelected: true
  },
  {
    id: 'doc_07',
    filename: '07_Attachment_D_-_Sample_Master_Agreement.docx',
    description: 'Attachment D - Sample Master Agreement',
    category: 'contract',
    fileFormat: 'docx',
    mandatory: true,
    scoringWeight: 'Legal Baseline',
    keyRequirementsSummary: 'Model master service contract language including payment terms (Net 30/45), warranty guarantees, audit inspection rights, dispute resolution, and public records disclosure standards.',
    complianceCheckpoints: [
      'Review all boilerplate terms against company risk posture',
      'Identify any required redline modifications for Attachment J submission',
      'Affirm compliance with State Prompt Payment Act and Open Records Act'
    ],
    extractedClauses: [
      'Invoices shall be submitted monthly upon state acceptance of milestone deliverables.',
      'Contractor guarantees 12-month post-deployment warranty on all developed software components.'
    ],
    defaultSelected: true
  },
  {
    id: 'doc_08',
    filename: '08_Attachment_D1_-_Sample_Master_Agreement_Cover_Page.docx',
    description: 'Attachment D1 - Sample Master Agreement Cover Page',
    category: 'contract',
    fileFormat: 'docx',
    mandatory: true,
    scoringWeight: 'Administrative Check',
    keyRequirementsSummary: 'Contract execution cover page identifying contracting parties, contract ceiling value, initial term (3 years) with two 1-year renewal options, and official signing block.',
    complianceCheckpoints: [
      'Signature block completed by authorized signatory',
      'Correct contract effective date and term period alignment'
    ],
    extractedClauses: [
      'Initial term is 36 months from effective date, with two optional 12-month renewals.'
    ],
    defaultSelected: true
  },
  {
    id: 'doc_09',
    filename: '09_Attachment_E_-_Participation_Information.docx',
    description: 'Attachment E - Participation Information',
    category: 'legal_terms',
    fileFormat: 'docx',
    mandatory: false,
    scoringWeight: 'Cooperative Scope (Bonus Reach)',
    keyRequirementsSummary: 'Cooperative purchasing provisions allowing local political subdivisions, universities, school districts, and other state agencies to piggyback on awarded master contract pricing.',
    complianceCheckpoints: [
      'Affirm willingness to extend identical contract pricing to political subdivisions',
      'Set up multi-tenant account provisioning and separate billing capability'
    ],
    extractedClauses: [
      'Contractor agrees to extend pricing, terms, and conditions to eligible participating entities.'
    ],
    defaultSelected: true
  },
  {
    id: 'doc_10',
    filename: '10_Attachment_F_-_Protest_Information.docx',
    description: 'Attachment F - Protest - Information',
    category: 'legal_terms',
    fileFormat: 'docx',
    mandatory: false,
    scoringWeight: 'Legal Reference',
    keyRequirementsSummary: 'Protest procedures, timelines, filing prerequisites, appeal grounds (arbitrary, capricious, or violation of procurement statutes), and administrative bond requirements.',
    complianceCheckpoints: [
      'Awareness of 10-day post-award notice protest window',
      'Knowledge of administrative appeal hierarchy'
    ],
    extractedClauses: [
      'Any interested party may protest solicitation or award within 10 calendar days of intent-to-award notice.'
    ],
    defaultSelected: true
  },
  {
    id: 'doc_11',
    filename: '11_Attachment_G_Offeror_Info._Acknowledgement___Cert.docx',
    description: 'Attachment G Offeror Information Acknowledgements and Certifications',
    category: 'certification',
    fileFormat: 'docx',
    mandatory: true,
    scoringWeight: 'Mandatory Submission (Pass/Fail)',
    keyRequirementsSummary: 'Formal vendor disclosures: Debarment/Suspension certification, Conflict of Interest disclosure, Drug-Free Workplace certification, Lobbying Restrictions affidavit, and Boycott certification.',
    complianceCheckpoints: [
      'Clear standing on Federal SAM.gov Excluded Parties List',
      'Conflict of interest disclosure statement executed',
      'Anti-lobbying certification signed'
    ],
    extractedClauses: [
      'Offeror certifies neither it nor its principals are presently debarred or suspended.',
      'No state employee has direct or indirect financial interest in this proposal.'
    ],
    defaultSelected: true
  },
  {
    id: 'doc_12',
    filename: '12_Attachment_H_-_Requirements_Response_Table.xlsx',
    description: 'Attachment H - Requirements Response Table',
    category: 'matrix',
    fileFormat: 'xlsx',
    mandatory: true,
    scoringWeight: 'Technical Evaluation Core (40% Weight)',
    keyRequirementsSummary: 'Exhaustive line-item requirement spreadsheet containing 45+ mandatory (M) and highly desirable (D) functional, technical, security, and SLA specifications requiring compliance responses.',
    complianceCheckpoints: [
      'Every requirement tagged as: [C] Fully Compliant, [E] Compliant with Exception, or [X] Exceeds',
      'Detailed technical narrative provided for every line item in column E',
      'Cross-reference each item to relevant section in Technical Proposal Volume'
    ],
    extractedClauses: [
      'Failure to respond to all mandatory requirements will result in immediate disqualification.',
      'Vague assertions of compliance without explanatory methodology will receive reduced scores.'
    ],
    defaultSelected: true
  },
  {
    id: 'doc_13',
    filename: '13_Attachment_I_-_Cost_Proposal_Form.xlsx',
    description: 'Attachment I - Cost Proposal Form',
    category: 'pricing',
    fileFormat: 'xlsx',
    mandatory: true,
    scoringWeight: 'Cost Evaluation (30% Total Score)',
    keyRequirementsSummary: 'Structured multi-tab cost workbook: Labor Category Rate Sheet, Deliverables-Based Milestone Fixed Pricing, Annual Maintenance & Software Licensing, and Travel/Incidental Cost Breakdown.',
    complianceCheckpoints: [
      'All formulas and cell totals mathematically accurate and un-corrupted',
      'Labor rates supported by market survey data and historical benchmarks',
      'Clear, transparent volume discount schedule included'
    ],
    extractedClauses: [
      'Cost proposal must be all-inclusive, covering all labor, travel, licensing, overhead, and materials.',
      'No hidden fees or post-award rate escalations will be accepted.'
    ],
    defaultSelected: true
  },
  {
    id: 'doc_14',
    filename: '14_Attachment_J_ProposedModification_to_SampleMasterAgreemt.docx',
    description: 'Attachment J Proposed Modifications to Sample Master Agreement',
    category: 'contract',
    fileFormat: 'docx',
    mandatory: false,
    scoringWeight: 'Risk & Legal Alignment',
    keyRequirementsSummary: 'Formal exception log template for offerors to submit specific redline modifications to the Sample Master Agreement with precise rationale and substitute language.',
    complianceCheckpoints: [
      'Only propose commercially standard, defensible legal exceptions',
      'Provide clear, constructive substitute language for every proposed change',
      'State-favorable compromise positions clearly outlined'
    ],
    extractedClauses: [
      'Any exceptions to contract terms not submitted in Attachment J are deemed waived by offeror.'
    ],
    defaultSelected: true
  },
  {
    id: 'doc_15',
    filename: '15_Attachment_K_-_Claim_of_Business_Confidentiality.docx',
    description: 'Attachment K - Claim of Business Confidentiality',
    category: 'legal_terms',
    fileFormat: 'docx',
    mandatory: false,
    scoringWeight: 'FOIA / Public Record Protection',
    keyRequirementsSummary: 'Official request and justification for trade secret or proprietary information redaction under the State Freedom of Information / Public Records Act.',
    complianceCheckpoints: [
      'Specific page and section citation for all confidential trade secrets',
      'Substantiated legal basis for why disclosure would cause competitive harm',
      'Provision of redacted public-inspection version alongside full proposal'
    ],
    extractedClauses: [
      'Cost models, proprietary algorithms, and customer references may be protected with valid justification.'
    ],
    defaultSelected: true
  },
  {
    id: 'doc_16',
    filename: '16_Attachment_L_-_Supplier_Bid_Submission_Guide.pdf',
    description: 'Attachment L - Supplier Bid Submission Guide',
    category: 'submission_guide',
    fileFormat: 'pdf',
    mandatory: true,
    scoringWeight: 'Submission Protocol (Pass/Fail)',
    keyRequirementsSummary: 'Step-by-step instructions for packaging files, naming conventions (Volume I_Admin.pdf, Volume II_Technical.pdf, Volume III_Cost.xlsx), file size limits (50MB/file), and electronic portal upload protocol.',
    complianceCheckpoints: [
      'Separate Cost Proposal file strictly isolated from Technical Volume',
      'Follow exact PDF/Excel naming conventions specified in Guide Section 3',
      'Submit at least 2 hours prior to deadline to prevent upload timeout'
    ],
    extractedClauses: [
      'Cost proposal must NEVER be included inside Technical Proposal document.',
      'Files must not be password protected or corrupted.'
    ],
    defaultSelected: true
  }
];
